import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Lightbulb, Check, Loader2 } from "lucide-react";
import { useHypotheses } from "@/hooks/useHypotheses";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface ImportHypothesesButtonProps {
  sessionId: string;
  workspaceId: string;
  hypotheses: string[];
}

export function ImportHypothesesButton({ 
  sessionId, 
  workspaceId, 
  hypotheses 
}: ImportHypothesesButtonProps) {
  const [isImporting, setIsImporting] = useState(false);
  const [isImported, setIsImported] = useState(false);
  const { toast } = useToast();

  const handleImport = async () => {
    if (hypotheses.length === 0) return;
    
    setIsImporting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Nicht eingeloggt");

      // Check which hypotheses already exist
      const { data: existing } = await supabase
        .from("session_hypotheses")
        .select("hypothesis")
        .eq("session_id", sessionId);

      const existingHypotheses = new Set(existing?.map(e => e.hypothesis) || []);
      const newHypotheses = hypotheses.filter(h => !existingHypotheses.has(h));

      if (newHypotheses.length === 0) {
        toast({
          title: "Bereits importiert",
          description: "Alle Hypothesen wurden bereits zum Tracking hinzugefügt.",
        });
        setIsImported(true);
        return;
      }

      const inserts = newHypotheses.map(hypothesis => ({
        session_id: sessionId,
        workspace_id: workspaceId,
        hypothesis,
        source: "report",
        created_by: user.id,
      }));

      const { error } = await supabase
        .from("session_hypotheses")
        .insert(inserts);

      if (error) throw error;

      toast({
        title: "Hypothesen importiert",
        description: `${newHypotheses.length} Hypothesen wurden zum Tracking hinzugefügt.`,
      });
      setIsImported(true);
    } catch (error: any) {
      toast({
        title: "Fehler beim Import",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsImporting(false);
    }
  };

  if (hypotheses.length === 0) return null;

  return (
    <Button
      variant={isImported ? "outline" : "secondary"}
      size="sm"
      onClick={handleImport}
      disabled={isImporting || isImported}
      className="gap-2"
    >
      {isImporting ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          Importiere...
        </>
      ) : isImported ? (
        <>
          <Check className="h-4 w-4 text-green-600" />
          Zum Tracking hinzugefügt
        </>
      ) : (
        <>
          <Lightbulb className="h-4 w-4" />
          Zum Hypothesen-Tracking hinzufügen
        </>
      )}
    </Button>
  );
}
